import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import { SelectTriggerOption } from '../components/TriggerTypeSelectorModal';
import { EventTriggerType } from '../domain';
import { UserIdentifierProperty } from '../types';

export type PendingTriggerId = string;

export interface PendingTrigger {
  id: PendingTriggerId;
  triggerType: EventTriggerType;
  triggerOption: SelectTriggerOption;
  formValues: Record<string, unknown>;
  userIdentifierProperty: UserIdentifierProperty;
  createdAt: number;
}

interface PendingTriggersState {
  pendingTriggers: PendingTrigger[];
  isSubmitting: boolean;
  submitError: string | null;
  editingTriggerId: PendingTriggerId | null;

  addTrigger: (trigger: Omit<PendingTrigger, 'id' | 'createdAt'>) => PendingTriggerId;
  updateTrigger: (id: PendingTriggerId, updates: Partial<Omit<PendingTrigger, 'id' | 'createdAt'>>) => void;
  removeTrigger: (id: PendingTriggerId) => void;
  clearAllTriggers: () => void;
  setEditingTriggerId: (id: PendingTriggerId | null) => void;
  setIsSubmitting: (value: boolean) => void;
  setSubmitError: (error: string | null) => void;
  reset: () => void;
}

const defaultState = {
  pendingTriggers: [] as PendingTrigger[],
  isSubmitting: false,
  submitError: null as string | null,
  editingTriggerId: null as PendingTriggerId | null,
};

export const usePendingTriggersStore = create<PendingTriggersState>()(
  immer((set) => ({
    ...defaultState,

    addTrigger: (trigger) => {
      const id = crypto.randomUUID();
      set((state) => {
        state.pendingTriggers.push({
          ...trigger,
          id,
          createdAt: Date.now(),
        });
      });
      return id;
    },

    updateTrigger: (id, updates) => {
      set((state) => {
        const index = state.pendingTriggers.findIndex((t) => t.id === id);
        if (index !== -1) {
          state.pendingTriggers[index] = {
            ...state.pendingTriggers[index],
            ...updates,
          };
        }
      });
    },

    removeTrigger: (id) => {
      set((state) => {
        state.pendingTriggers = state.pendingTriggers.filter((t) => t.id !== id);
      });
    },

    clearAllTriggers: () => {
      set((state) => {
        state.pendingTriggers = [];
      });
    },

    setEditingTriggerId: (id) => set({ editingTriggerId: id }),
    setIsSubmitting: (value) => set({ isSubmitting: value }),
    setSubmitError: (error) => set({ submitError: error }),

    reset: () => set(defaultState),
  })),
);
